import React, { FunctionComponent, useState, useMemo } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonTitle,
  IonToolbar,
  IonSpinner,
} from '@ionic/react';
import { close, chevronDown, chevronForward, searchOutline, closeOutline } from 'ionicons/icons';
import { MetaLibrarySystemModel, MetaLibrarySubSystemModel } from '../models/meta-library.model';

interface SystemsModalProps {
  isOpen: boolean;
  systems: MetaLibrarySystemModel[];
  isLoading: boolean;
  onClose: () => void;
  onSubSystemSelect: (subSystem: MetaLibrarySubSystemModel, systemCode: string) => void;
}

export const SystemsModal: FunctionComponent<SystemsModalProps> = ({
  isOpen,
  systems,
  isLoading,
  onClose,
  onSubSystemSelect,
}) => {
  const [expandedSystemIds, setExpandedSystemIds] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const toggleSystem = (systemId: string) => {
    const newExpanded = new Set(expandedSystemIds);
    if (newExpanded.has(systemId)) {
      newExpanded.delete(systemId);
    } else {
      newExpanded.add(systemId);
    }
    setExpandedSystemIds(newExpanded);
  };

  const handleSubSystemClick = (subSystem: MetaLibrarySubSystemModel, systemCode: string) => {
    onSubSystemSelect(subSystem, systemCode);
  };

  const handleSearchChange = (e: CustomEvent) => {
    const value = e.detail.value!;
    setSearchText(value);
  };

  const clearSearch = () => {
    setSearchText('');
  };

  // Filtrer les systèmes et sous-systèmes en fonction de la recherche
  const filteredSystems = useMemo(() => {
    if (!searchText.trim()) {
      return systems;
    }

    const searchLower = searchText.toLowerCase();
    
    return systems.map(system => {
      const systemMatches = 
        system.code.toLowerCase().includes(searchLower) ||
        system.system.toLowerCase().includes(searchLower);

      // Si le système correspond, on garde tous ses sous-systèmes
      if (systemMatches) {
        return system;
      }

      // Sinon, filtrer les sous-systèmes
      const filteredSubSystems = system.metaLibrarySubSystems.filter(subSystem => {
        const fullCode = `${system.code}${subSystem.code}`;
        return fullCode.toLowerCase().includes(searchLower) ||
               subSystem.subSystem.toLowerCase().includes(searchLower);
      });

      // Si des sous-systèmes correspondent, retourner le système avec uniquement ces sous-systèmes
      if (filteredSubSystems.length > 0) {
        return {
          ...system,
          metaLibrarySubSystems: filteredSubSystems
        };
      }

      // Aucune correspondance, on exclut ce système
      return null;
    }).filter(system => system !== null) as MetaLibrarySystemModel[];
  }, [systems, searchText]);

  return (
    <IonModal isOpen={isOpen}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Systèmes</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <IonSpinner />
          </div>
        ) : (
          <>
            {/* Champ de recherche */}
            <div style={{ marginBottom: '16px' }}>
              <IonItem
                lines="none"
                style={{
                  width: '100%',
                  background: '#F7F8FB',
                  border: '1px solid #ccc',
                  borderRadius: '100px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  alignItems: 'center',
                  height: '40px',
                  display: 'flex',
                }}
              >
                <IonIcon
                  icon={searchOutline}
                  slot="start"
                  style={{
                    fontSize: '16px',
                    color: !searchText ? '#888' : '#fff',
                    backgroundColor: !searchText
                      ? 'transparent'
                      : isSearchFocused
                        ? '#165BE6'
                        : '#4B4D55',
                    borderRadius: '50%',
                    padding: '4px',
                    height: '24px',
                    width: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.3s',
                    marginRight: '6px',
                    verticalAlign: 'middle',
                  }}
                />

                <IonInput
                  placeholder="Rechercher un système ou sous-système"
                  value={searchText}
                  onIonInput={handleSearchChange}
                  onIonFocus={() => setIsSearchFocused(true)}
                  onIonBlur={() => setIsSearchFocused(false)}
                  style={{
                    font: 'open-sans, sans-serif',
                    weight: '400',
                    background: 'transparent',
                    flex: 1,
                    fontSize: '12px',
                    lineHeight: '100%',
                    verticalAlign: 'middle',
                    height: '100%',
                    alignItems: 'center',
                  }}
                  clearInput={false}
                  role="search"
                  enterkeyhint="search"
                />

                {searchText && (
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={clearSearch}
                    style={{
                      padding: '2px',
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      verticalAlign: 'middle',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IonIcon
                      icon={closeOutline}
                      slot="icon-only"
                      style={{
                        fontSize: '14px',
                        color: '#888',
                        backgroundColor: 'transparent',
                      }}
                    />
                  </IonButton>
                )}
              </IonItem>
            </div>

            <IonList>
              {filteredSystems.map((system) => {
              const isExpanded = expandedSystemIds.has(system.systemTypeId);
              return (
                <div key={system.systemTypeId}>
                  <IonItem
                    button
                    onClick={() => toggleSystem(system.systemTypeId)}
                    detail={false}
                  >
                    <IonIcon
                      icon={isExpanded ? chevronDown : chevronForward}
                      slot="start"
                      style={{ marginRight: '8px' }}
                    />
                    <IonLabel>
                      <h2 style={{ fontWeight: 'bold' }}>
                        {system.code} - {system.system}
                      </h2>
                    </IonLabel>
                  </IonItem>

                  {isExpanded && system.metaLibrarySubSystems.map((subSystem) => (
                    <IonItem
                      key={subSystem.subSystemTypeId}
                      button
                      onClick={() => handleSubSystemClick(subSystem, system.code)}
                      style={{ paddingLeft: '40px' }}
                      detail={true}
                      detailIcon={chevronForward}
                    >
                      <IonLabel>
                        <h3>
                          {system.code}{subSystem.code} - {subSystem.subSystem}
                        </h3>
                      </IonLabel>
                    </IonItem>
                  ))}
                </div>
              );
            })}
            </IonList>
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

